/**
 * MIRROR OF `Core/Modules/Protocol/Protocol.h` AND `Core/Modules/Config/Config.h`.
 *
 *   ####################################################################
 *   #  DO NOT EDIT THIS FILE IN ISOLATION.                             #
 *   #  `bun run check:protocol` parses those headers and fails on any   #
 *   #  drift. `bun run test` runs it. Change the header first.          #
 *   ####################################################################
 *
 * Everything here is a wire constant. Behaviour lives in codec.ts and transport.ts.
 */

/* ---------------------------------------------------------------- framing -- */

/** Bytes of MsgFrame.data. */
export const MSG_PAYLOAD_SIZE = 59;
/** sizeof(MsgFrame) = 1 + 1 + 2 + 59. */
export const MSG_FRAME_SIZE = 63;
/** What actually goes on the wire: MSG_FRAME_SIZE + the leading report ID. */
export const MSG_REPORT_SIZE = 64;

/**
 * Report IDs on interface 1.
 *
 * FRAMING CONTRACT (measured, see AGENTS.md section 6.6) -- the asymmetry is real
 * and it goes opposite ways for the two directions:
 *
 *   sendReport(id, data)          -> data EXCLUDES the report ID (63 bytes)
 *   the device's OutEvent buffer  -> INCLUDES it (64 bytes, buf[0] === id)
 *   the 'inputreport' event       -> data EXCLUDES it (event.reportId is separate)
 *   receiveFeatureReport(id)      -> INCLUDES it (byteLength 64, byte[0] === id)
 *
 * codec.ts is the single place that reconciles this. Do not spread it around.
 */
export const MSG_REPORT_ID_OUT = 1;
export const MSG_REPORT_ID_IN = 2;
export const MSG_REPORT_ID_FEATURE = 3;

/* ---------------------------------------------------------------- commands -- */

/** host -> device */
export const MsgId = {
  PING: 0x01,
  GET_INFO: 0x02,
  GET_CONFIG: 0x03,
  SET_CONFIG: 0x04,
  PATTERN_BEGIN: 0x05,
  PATTERN_DATA: 0x06,
  PATTERN_END: 0x07,
  SAVE_TO_FLASH: 0x08,
  LOAD_FROM_FLASH: 0x09,
  FACTORY_RESET: 0x0a,
  START: 0x0b,
  STOP: 0x0c,
  GET_PATTERN: 0x0d,
  GET_STATUS: 0x0e,
} as const;

/** device -> host */
export const RespId = {
  ACK: 0x80,
  NACK: 0x81,
  INFO: 0x82,
  CONFIG: 0x83,
  STATUS: 0x84,
  PATTERN_CHUNK: 0x85,
  SAVE_RESULT: 0x86,
} as const;

export type MsgIdValue = (typeof MsgId)[keyof typeof MsgId];
export type RespIdValue = (typeof RespId)[keyof typeof RespId];

/** Human-readable name for any msgId, for logs and error messages. */
export const MSG_ID_NAMES: Readonly<Record<number, string>> = Object.freeze({
  ...Object.fromEntries(Object.entries(MsgId).map(([k, v]) => [v, k])),
  ...Object.fromEntries(Object.entries(RespId).map(([k, v]) => [v, k])),
});

export function msgIdName(id: number): string {
  return MSG_ID_NAMES[id] ?? `0x${id.toString(16).padStart(2, '0')}`;
}

/* --------------------------------------------------------- nack reasons -- */

export const NackReason = {
  UNKNOWN_CMD: 0x01,
  BAD_LENGTH: 0x02,
  TOO_LONG: 0x03,
  BAD_PARAM: 0x04,
  BAD_STATE: 0x05,
  FLASH_ERROR: 0x06,
  BUSY: 0x07,
  CRC_ERROR: 0x08,
} as const;

export const NACK_REASON_NAMES: Readonly<Record<number, string>> = Object.freeze(
  Object.fromEntries(Object.entries(NackReason).map(([k, v]) => [v, k])),
);

/** Explanations shown next to a NACK in the UI. The device sends only a byte. */
export const NACK_REASON_HELP: Readonly<Record<number, string>> = Object.freeze({
  [NackReason.UNKNOWN_CMD]: 'The device does not recognise that command. Firmware and app are probably out of step.',
  [NackReason.BAD_LENGTH]: 'The command payload was the wrong size.',
  [NackReason.TOO_LONG]: 'The payload exceeded 59 bytes. Every command must fit in one 64-byte report.',
  [NackReason.BAD_PARAM]: 'A value was out of range (kind, duration, amplitude, step count or boot delay).',
  [NackReason.BAD_STATE]: 'The device was not in a state where that command is meaningful.',
  [NackReason.FLASH_ERROR]: 'The flash erase or program step failed. Nothing was saved.',
  [NackReason.BUSY]: 'The device was busy. Try again.',
  [NackReason.CRC_ERROR]: 'The stored configuration failed its CRC check.',
});

/* ---------------------------------------------------------- capabilities -- */

export const Cap = {
  PATTERN_MACRO: 0x0001,
  PATTERN_CIRCLE: 0x0002,
  PATTERN_ZIGZAG: 0x0004,
  PATTERN_RANDWALK: 0x0008,
  PATTERN_DRIFT: 0x0010,
  FLASH_PERSIST: 0x0020,
  FEATURE_REPORT: 0x0040,
} as const;

/* ------------------------------------------------------------ config wire -- */

export const MSG_CFG_HEADER_SIZE = 20;

export const ConfigOffset = {
  KIND: 0,
  FLAGS: 1,
  JITTER: 2,
  SEED: 3,
  DURATION_S: 4,   // u32 LE
  AMPLITUDE: 8,    // u16 LE
  PERIOD_MS: 10,   // u16 LE
  STEP_COUNT: 12,  // u16 LE
  BOOT_DELAY_MS: 14, // u16 LE
  SAVED: 16,
} as const;

/* ---------------------------------------------------------- step payload -- */

/** PATTERN_BEGIN: kind:u8, stepCount:u16 */
export const MSG_PB_SIZE = 3;
/**
 * PATTERN_DATA and PATTERN_CHUNK share a shape:
 *   offset/stepIndex:u16, count:u8, then `count` packed steps of 6 bytes.
 * 2 + 1 + 9*6 = 57 <= 59, which is why a pattern moves 9 steps per frame with
 * no reassembly on the device.
 */
export const MSG_STEPS_OFF_INDEX = 0;
export const MSG_STEPS_OFF_COUNT = 2;
export const MSG_STEPS_OFF_FIRST = 3;
export const MSG_STEPS_PER_FRAME = 9;
export const MSG_STEP_WIRE_SIZE = 6;

/* --------------------------------------------------------------- responses -- */

export const MSG_INFO_SIZE = 8;
export const MSG_STATUS_SIZE = 15;
export const MSG_SAVE_RESULT_SIZE = 2;

/** GET_PATTERN request: offset:u16, count:u8. The RESPONSE is always a
 *  PATTERN_CHUNK, with count possibly 0 for end-of-data. */
export const MSG_GP_SIZE = 3;

/* INFO: fwMajor, fwMinor, fwPatch, protoVer, caps:u16, state, saved */
export const MSG_INFO_OFF_FW_MAJOR = 0;
export const MSG_INFO_OFF_FW_MINOR = 1;
export const MSG_INFO_OFF_FW_PATCH = 2;
export const MSG_INFO_OFF_PROTO = 3;
export const MSG_INFO_OFF_CAPS = 4;
export const MSG_INFO_OFF_STATE = 6;
export const MSG_INFO_OFF_SAVED = 7;

/* STATUS: state:u8, elapsedMs:u32, totalMs:u32, stepIndex:u16, stepsDone:u32 */
export const MSG_STATUS_OFF_STATE = 0;
export const MSG_STATUS_OFF_ELAPSED_MS = 1;
export const MSG_STATUS_OFF_TOTAL_MS = 5;
export const MSG_STATUS_OFF_STEP_INDEX = 9;
export const MSG_STATUS_OFF_STEPS_DONE = 11;

/* ----------------------------------------------------------------- domain -- */

/** Mirrors PatternKind in Config.h. */
export const PatternKind = {
  MACRO: 0,
  CIRCLE: 1,
  ZIGZAG: 2,
  RANDWALK: 3,
  DRIFT: 4,
} as const;

export const PATTERN_KIND_NAMES: Readonly<Record<number, string>> = Object.freeze({
  [PatternKind.MACRO]: 'MACRO',
  [PatternKind.CIRCLE]: 'CIRCLE',
  [PatternKind.ZIGZAG]: 'ZIGZAG',
  [PatternKind.RANDWALK]: 'RANDWALK',
  [PatternKind.DRIFT]: 'DRIFT',
});

/** Mirrors JigglerState in Jiggler.h. */
export const JigState = {
  BOOT_WAIT: 0,
  RUNNING: 1,
  DONE: 2,
  STOPPED: 3,
} as const;

export const JIG_STATE_NAMES: Readonly<Record<number, string>> = Object.freeze({
  [JigState.BOOT_WAIT]: 'BOOT_WAIT',
  [JigState.RUNNING]: 'RUNNING',
  [JigState.DONE]: 'DONE',
  [JigState.STOPPED]: 'STOPPED',
});

/** Mirrors Config.h limits. */
export const CFG_MAX_STEPS = 256;
export const CFG_BOOT_DELAY_DEFAULT_MS = 5000;
export const CFG_BOOT_DELAY_MAX_MS = 60000;
export const CFG_MIN_STEP_MS = 2;
export const CFG_MS_PER_SEC = 1000;
/**
 * Largest generator amplitude. A SANITY BOUND ONLY -- the real constraint is the
 * per-step delta, enforced by `domain/generators.ts`. Note this must stay ABOVE the
 * derived limits (1294 for CIRCLE, 2032 for the linear generators) or that check
 * could never fire. NOT 127: that is the per-report delta limit, and amplitude is a
 * POSITION bound, not a delta.
 */
export const CFG_MAX_AMPLITUDE = 4096;

/* ------------------------------------------------------------------ device -- */

/**
 * USB identity. Must match usbd_desc.c.
 *
 * NEVER add a filter for the mouse interface. It is a separate USB HID interface
 * whose only collection is Generic Desktop / Mouse, which WebHID blocklists --
 * Chromium exposes it but strips its reports (the collection arrives EMPTY). It
 * is enumerated and even openable, so a naive getDevices()[0] would appear to
 * work and then fail every call. See AGENTS.md sections 6.1 and 10.2.
 */
export const VENDOR_ID = 0x0483;
export const PRODUCT_ID = 0x5730;

export const DEVICE_FILTER: HIDDeviceFilter = {
  vendorId: VENDOR_ID,
  productId: PRODUCT_ID,
  usagePage: 0xff00,
  usage: 0x01,
};
/** True when this is one of our USB interfaces at all (any of them). */
export function isOurDevice(d: HIDDevice): boolean {
  return d.vendorId === VENDOR_ID && d.productId === PRODUCT_ID;
}

/** True when this HIDDevice is the vendor configuration interface. */
export function isConfigInterface(d: HIDDevice): boolean {
  return (
    isOurDevice(d) &&
    d.collections.some((c) => c.usagePage === 0xff00 && c.usage === 0x01)
  );
}

/**
 * Should we try to attach to this device on a `connect` event?
 *
 * Stricter than `isOurDevice` because the MOUSE interface also fires `connect`, and
 * opening it succeeds (its collection arrives empty rather than the device being
 * hidden) before every I/O call fails.
 *
 * Falls back to accepting when `collections` is empty: normally Chrome has parsed the
 * report descriptor by the time the event fires, but if it has not, a wrong guess
 * fails loudly at the PING rather than silently attaching to the wrong interface.
 */
export function shouldAttach(d: HIDDevice): boolean {
  if (!isOurDevice(d)) return false;
  if (d.collections.length === 0) return true;
  return d.collections.some((c) => c.usagePage === 0xff00 && c.usage === 0x01);
}

/** Human-readable labels for the browser-support diagnostics. */
export const REQUIREMENTS = {
  browser: 'Chrome or Edge 89+',
  secureContext: 'HTTPS or localhost',
} as const;
